/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { FaInfo, FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { ContactApi } from "../../../api/Contacts";
import { createEmail } from "../../../api/Email";
import { updateUser } from "../../../api/User";
import { ActiveEmailAtom } from "../../../atoms/ActiveEmailAtom";
import { ContactFiltersAtom } from "../../../atoms/ContactFiltersAtom";
import { InternalUserAtom, UserAtom } from "../../../atoms/UserAtom";
import { IAlias, IFooter, IUserIRoute } from "../../../generated/Api";
import { useDefaultAlias } from "../../../hook/useDefaultAlias";
import { FilterByEmailAtom } from "../../../pages/contacts";
import { getBody } from "../../../pages/new-email";
import { showHeaderCSS } from "../../../styles/general";
import { IOption } from "../../../types/CommonTypes";
import { convertVariables } from "../../../utils/ConvertVariables";
import { FormatEmail } from "../../../utils/FormatEmail";
import { validationEmail } from "../../../utils/Validation";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import { useModal } from "../Modal";
import AddContact from "./AddContact";
import Warning from "./Warning";

const IconButton = styled.div`
  height: 32px;
  width: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;

  border: 1px solid #dddddd;

  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;
  margin: 16px -24px 0px;
  width: 1000px;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 93px);
    margin: 0;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;
  max-height: 70vh;
  background-color: #f4f4f4;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding: 32px 24px;

  @media screen and (max-width: 1024px) {
    height: 100%;
    max-height: 100%;
  }
`;

const Buttons = styled.div`
  width: 100%;
  background-color: #fff;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;

  @media screen and (max-width: 1024px) {
    position: absolute;
    bottom: 25px;
    left: 0;
    right: 0;

    justify-content: start;
    grid-gap: 16px;
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: bold;
  width: 100%;
  margin-bottom: 12px;
  font-size: 24px;
`;

const ItemToolbarContainer = styled.div`
  display: flex;
  grid-gap: 8px;

  position: absolute;
  right: 10px;

  align-items: center;
  margin-top: -6px;
`;

const IconButtonWithHover = styled(IconButton)`
  color: #aaaaaa !important;

  &:hover {
    color: #fb972e !important;
  }
`;

export const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const SendFromOnTop = styled.div`
  max-width: 100%;

  margin-right: auto;
  margin-top: -15px;
  padding-bottom: 15px;
`;

interface Props {
  subject: string;
  body: string;
  user: IUserIRoute;
  emailInfo: { footer: IFooter; alias: IAlias };
  editor?: Editor;
  isCampaign?: boolean;
}

export default function PreviewEmail(props: Props): JSX.Element | null {
  const { body, subject, emailInfo, editor, isCampaign } = props;

  const setContactFilters = useSetRecoilState(ContactFiltersAtom);
  const setFilterByEmail = useSetRecoilState(FilterByEmailAtom);

  const [user, setUser] = useRecoilState(UserAtom);
  const internalUser = useRecoilValue(InternalUserAtom);
  const newEmail = useRecoilValue(ActiveEmailAtom);
  const router = useRouter();

  const modal = useModal();

  // As close to server code as possible
  const style = body.split('pre style="')[1]?.split('"')[0] || null;

  const [selectedValue, setSelectedValue] = useState(
    user.options.testEmailRecipient && user.options.testEmailRecipient[0]
      ? user.options.testEmailRecipient[0]
      : ""
  );
  const [testEmailOption, setTestEmailOption] = useState<IOption>(
    user.options.testEmailRecipient && user.options.testEmailRecipient[0]
      ? {
          name: user.options.testEmailRecipient[0],
          value: user.options.testEmailRecipient[0],
        }
      : {
          name: "",
          value: "",
        }
  );

  const { defaultAlias } = useDefaultAlias({
    user,
    internalUser,
  });

  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const options = useMemo(
    () =>
      user.options.testEmailRecipient && user.options.testEmailRecipient.map
        ? user.options.testEmailRecipient.map((e) => ({
            name: e,
            value: e,
          }))
        : [],
    [user.options.testEmailRecipient]
  );

  useEffect(() => {
    let isEmailValid = validationEmail(selectedValue);

    setIsValidEmail(isEmailValid);
  }, [testEmailOption, selectedValue]);

  async function handleCanSendTestEmail(email: string) {
    const { contacts, total } = await ContactApi.read({
      limit: 1,
      search: email,
    });

    if (contacts && contacts.length > 0) return true;
    return false;
  }

  async function deleteTestEmail(email: string) {
    const updatedUser: IUserIRoute = {
      ...user,
      options: {
        ...user.options,
        testEmailRecipient: user.options.testEmailRecipient
          ? user.options.testEmailRecipient.filter((e) => e !== email)
          : [],
      },
    };

    // Put the email being sent at the front, so it's the first email
    await updateUser(updatedUser);
    setUser(updatedUser as any);

    // If it's on the input, clear it
    if (email === selectedValue) {
      setSelectedValue("");
    }
  }

  async function handleSendTestEmail() {
    if (!editor) return null;

    let body = getBody(editor, user);

    let newEmailSend = {
      ...newEmail,
      body,
      subject,
    };

    let selectedFooterId =
      emailInfo.footer?.id || user.emailOptions.footers[0].id;
    let selectedAliasId = emailInfo.alias?.id || defaultAlias.id;

    const updatedUser: IUserIRoute = {
      ...user,
      options: {
        ...user.options,
        testEmailRecipient: user.options.testEmailRecipient
          ? Array.from(
              new Set([selectedValue, ...user.options.testEmailRecipient])
            )
          : [selectedValue],
      },
    };

    // Put the email being sent at the front, so it's the first email
    await updateUser(updatedUser);
    setUser(updatedUser as any);

    const response = await createEmail(
      {
        ...newEmailSend,
        ...(isCampaign && { labels: [] }),
        footerId: selectedFooterId,
        aliasId: selectedAliasId,
        defaultModeColorLink: user.emailOptions.defaultModeColorLink,
        darkModeColorLink: user.emailOptions.darkModeColorLink,
        toList: [selectedValue],
        tagQuery: undefined,
        searchId: undefined,
        showOnArchive: false,
        splitSubject: undefined,
        splitMode: undefined,
      },
      "test"
    );

    // If the option is blank, then I used the value in the input to autocreate an option
    // so I should create an option here and select it
    if (testEmailOption.value !== selectedValue) {
      setTestEmailOption({
        value: selectedValue,
        name: selectedValue,
      });
    }
  }

  let { linkedBody, footer } = FormatEmail.format(body, user, emailInfo);

  linkedBody = convertVariables(linkedBody);

  return (
    <ModalBody>
      <Wrapper>
        <SendFromOnTop>
          Send From:
          {emailInfo.alias
            ? ` ${emailInfo.alias.fromName} <${emailInfo.alias.address}>`
            : ` ${defaultAlias.fromName} <${defaultAlias.address}>`}
        </SendFromOnTop>
        {subject && subject.trim() !== "" && <Title>{subject}</Title>}

        <div
          css={css`
            width: 100%;

            pre:first-child {
              ${style};
            }

            * {
              /* Sets the width to max configured by the user if any */
              max-width: ${user?.emailOptions.maxWidth
                ? `${user?.emailOptions.maxWidth}px`
                : "100%"};
              font-size: medium;
              box-sizing: content-box;
              line-height: 1.5em;
              letter-spacing: 0.025em;

              /* And also, if there's a max, then it should 
                 be "auto" (solves ul > li > p being more than .maxWidth) 
               */
              width: ${user?.emailOptions.maxWidth ? `auto` : "100%"};

              ${showHeaderCSS}

              li > p {
                all: unset;
              }
            }
          `}
        >
          <div
            css={css`
              width: 100%;
              ul[data-type="dashedList"] {
                position: relative;
                list-style-type: none;

                > li {
                  margin: 15px 0px;
                }

                > li::before {
                  content: "_";
                  position: absolute;
                  margin-left: -18px;
                  margin-top: -6px;
                }
              }
            `}
            dangerouslySetInnerHTML={{
              __html:
                `${linkedBody}\n` +
                `<pre style="overflow-wrap: break-word; white-space: pre-wrap; width:100%;${
                  style ? style : "font: small/ 1.5  Arial,Helvetica,sans-serif"
                }; font-size: small; 
                ">${footer}</pre>`,
            }}
          />
        </div>
      </Wrapper>

      <Buttons>
        {editor && (
          <div
            css={css`
              display: flex;
              grid-gap: 16px;
            `}
          >
            <div
              css={css`
                width: 420px;

                @media screen and (max-width: 1024px) {
                  width: 100%;
                }

                .css-11pjhgr-ListItemText {
                  text-overflow: ellipsis;
                  max-width: 275px;
                  overflow: hidden;
                  white-space: nowrap;
                }
              `}
            >
              <StyledAutocompleteSelect
                hideNoOption
                selectedOption={testEmailOption}
                onOptionSelect={(option) => {
                  setTestEmailOption(option);
                }}
                placeholder="Test email recipient"
                selectedValue={selectedValue}
                setSelectedValue={(text) => setSelectedValue(text)}
                options={options}
                resetOnClosedDropview={false}
                removeCheckIcon
                renderCustomItemToolbar={(option) => (
                  <ItemToolbarContainer>
                    <IconButtonWithHover
                      data-tip
                      data-for="info"
                      onClick={() => {
                        setContactFilters((oldFilter) => ({
                          ...oldFilter,
                          search: option.value.toLowerCase().trim() as string,
                        }));
                        setFilterByEmail(
                          option.value.toLowerCase().trim() as string
                        );

                        modal.popModal();

                        router.push(`/contacts/?search=${option.value}`);
                      }}
                    >
                      <FaInfo />
                      <ReactTooltip
                        id="info"
                        place="top"
                        effect="solid"
                        backgroundColor="#333333"
                        css={css`
                          padding: 4px 8px;
                        `}
                      >
                        <TooltipText>Contact Information</TooltipText>
                      </ReactTooltip>
                    </IconButtonWithHover>

                    <IconButtonWithHover
                      data-tip
                      data-for="edit"
                      onClick={() => {
                        setContactFilters((oldFilter) => ({
                          ...oldFilter,
                          search: option.value.toLowerCase().trim() as string,
                        }));

                        setFilterByEmail(
                          option.value.toLowerCase().trim() as string
                        );

                        modal.popModal();

                        router.push(
                          `/contacts/?search=${option.value}&modal=true`
                        );
                      }}
                    >
                      <FaPencilAlt />
                      <ReactTooltip
                        id="edit"
                        place="top"
                        effect="solid"
                        backgroundColor="#333333"
                        css={css`
                          padding: 4px 8px;
                        `}
                      >
                        <TooltipText>Edit Contact</TooltipText>
                      </ReactTooltip>
                    </IconButtonWithHover>

                    <IconButtonWithHover
                      data-tip
                      data-for="delete"
                      onClick={() =>
                        modal.pushModal(
                          <Warning
                            icon={"/icons/delete.svg"}
                            title="Delete Contact"
                            description={`Are you sure you want delete this contact of test list?`}
                            buttonsName={["No", "Yes"]}
                            onOkClicked={() => {
                              deleteTestEmail(option.value);
                              modal.popModal();
                            }}
                            onCancelClicked={() => {
                              modal.popModal();
                            }}
                          />
                        )
                      }
                    >
                      <FaRegTrashAlt />
                      <ReactTooltip
                        id="delete"
                        place="top"
                        effect="solid"
                        backgroundColor="#333333"
                        css={css`
                          padding: 4px 8px;
                        `}
                      >
                        <TooltipText>Delete Contact</TooltipText>
                      </ReactTooltip>
                    </IconButtonWithHover>
                  </ItemToolbarContainer>
                )}
              />
            </div>

            <StyledButton
              isLoading={isLoading}
              disable={!isValidEmail}
              onClick={async () => {
                let selectedAlias = defaultAlias;
                if (
                  testEmailOption.value === selectedAlias.address ||
                  selectedValue === selectedAlias.address
                ) {
                  toast(
                    `The recipient can't be the same address as you're sending from <${selectedAlias.address}>`,
                    { type: "warning" }
                  );
                  return;
                }

                setIsLoading(true);

                try {
                  const continueSendEmail = await handleCanSendTestEmail(
                    selectedValue
                  );

                  if (continueSendEmail) {
                    await handleSendTestEmail();
                    toast(`Email sent successfully to ${selectedValue}`, {
                      type: "success",
                    });
                  } else {
                    modal.pushModal(
                      <Warning
                        icon={"/icons/warning.svg"}
                        title="Create Contact"
                        description={`You can only send emails to contacts. Would you like to create a new one with ${selectedValue}? `}
                        buttonsName={["No", "Yes"]}
                        onOkClicked={() => {
                          modal.popModal();
                          modal.pushModal(
                            <AddContact emailFromTest={selectedValue} />,
                            "Add new contact"
                          );
                        }}
                        onCancelClicked={() => {
                          modal.popModal();
                        }}
                      />
                    );
                  }
                } catch (err) {
                } finally {
                  setIsLoading(false);
                }
              }}
              css={css`
                width: fit-content;
                height: 43px;
                padding: 0 16px;
                flex-shrink: 0;
              `}
            >
              Send Test
            </StyledButton>
          </div>
        )}

        <StyledButton
          inverted
          onClick={() => modal.popModal()}
          css={css`
            width: fit-content;
            height: 43px;
            padding: 0 16px;
            flex-shrink: 0;
          `}
        >
          Close
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
